import { useCallback, useEffect, useRef, useState } from 'react';
import { useToast } from '../components/Toast/ToastContext';
import { getPlan } from './api';
import { canManage } from './permissions';
import type { PlanLoadError, PlanResponse, PlanRole, PresenceUser } from './types';
import { useNodePlanLive } from './useNodePlanLive';
import type { LiveStatus } from './useNodePlanLive';
import { useUnread } from './useUnread';
import type { Unread } from './useUnread';

const classify = (e: unknown): PlanLoadError => {
  const err = e as { status?: number; data?: { error?: { code?: string } } } | null;
  if (err?.status === 403 && err?.data?.error?.code === 'not_a_member') return 'not_a_member';
  if (err?.status === 404) return 'not_found';
  if (err?.status === 401) return 'signed_out';
  return 'failed';
};

export interface UseNodePlan {
  data: PlanResponse | null;
  role: PlanRole | null;
  canManage: boolean;
  loading: boolean;
  error: PlanLoadError | null;
  reload: () => Promise<void>;
  /** Who has the plan open right now, this person included. */
  presence: PresenceUser[];
  /** Per-node counts of what changed since this person last looked. */
  unread: Unread;
  /** The newest plan version the live stream has announced; may run ahead of `data`. */
  liveVersion: number | null;
  liveStatus: LiveStatus;
  /** Done topics in the plan, counted whether or not they are shown. */
  doneCount: number;
}

export function useNodePlan(planId: string, { showDone = false }: { showDone?: boolean } = {}): UseNodePlan {
  const toast = useToast();
  const [data, setData] = useState<PlanResponse | null>(null);
  const [error, setError] = useState<PlanLoadError | null>(null);
  const [loading, setLoading] = useState(true);
  // Only the newest request may write state; unmount bumps it so a late
  // response lands nowhere.
  const latest = useRef(0);
  const loadedVersion = useRef<number | null>(null);
  const unread = useUnread(planId, false);
  const refreshUnread = unread.refresh;
  // Read at request time, not captured: a write's reload that lands after the switch
  // flipped must fetch the view the switch now says.
  const showDoneRef = useRef(showDone);
  showDoneRef.current = showDone;
  const hasData = useRef(false);

  const reload = useCallback(async () => {
    const req = ++latest.current;
    try {
      const res = await getPlan(planId, { done: showDoneRef.current });
      if (req !== latest.current) return;
      loadedVersion.current = res.plan.version;
      hasData.current = true;
      setData(res);
      setError(null);
      refreshUnread();
    } catch (e) {
      if (req !== latest.current) return;
      setError(classify(e));
      // Over a plan already on screen the error state never shows, so say it here.
      if (hasData.current) toast.error('Could not refresh the plan. What you see may be behind.');
    } finally {
      if (req === latest.current) setLoading(false);
    }
  }, [planId, showDone, refreshUnread, toast]);

  // Only a different plan starts from blank. Toggling Show done refetches over what is
  // on screen, so the document never drops to the loading state and loses its place.
  useEffect(() => {
    setData(null);
    setError(null);
    setLoading(true);
    loadedVersion.current = null;
    hasData.current = false;
  }, [planId]);

  useEffect(() => {
    reload();
    return () => {
      latest.current += 1;
    };
  }, [reload]);

  const live = useNodePlanLive(planId, {
    onChanged: (version) => {
      // Our own writes reload as they land; their echo from the stream is already here.
      if (version !== null && loadedVersion.current !== null && version <= loadedVersion.current) return;
      reload();
    },
    onRemoved: () => {
      latest.current += 1;
      setData(null);
      setError('not_a_member');
      setLoading(false);
    },
    onTooManyStreams: () =>
      toast.info('Live updates are paused: this plan is open in too many places. Close it somewhere else and they resume.'),
  });

  const role = data?.role ?? null;
  return {
    data,
    role,
    canManage: canManage(role),
    loading,
    error,
    reload,
    presence: live.presence,
    unread,
    liveVersion: live.version,
    liveStatus: live.status,
    doneCount: data?.done_count ?? 0,
  };
}
